import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ClipboardList, Hammer, Sofa, Layers } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { EXTERIOR, FOUNDER, OAK, MARBLE, HOME_FEATURE, PARRIWI_GALLERY, CONTACT } from "@/lib/assets";

const ease = [0.22, 1, 0.36, 1];

const services = [
  { icon: ClipboardList, title: "Renovation Project Management", text: "End-to-end coordination of your renovation, from first plan to final detail." },
  { icon: Hammer, title: "Trade Coordination", text: "A trusted team of trades, scheduled and managed so the work flows seamlessly." },
  { icon: Sofa, title: "Interior Selections", text: "Considered selections that bring warmth, cohesion and a sense of calm to every room." },
  { icon: Layers, title: "Material & Finish Sourcing", text: "Sourcing natural materials and refined finishes that stand the test of time." },
];

const process = [
  { n: "01", title: "Discover", text: "We begin with a conversation — understanding your home, your vision and how you live." },
  { n: "02", title: "Plan", text: "A clear, considered plan: scope, selections, timelines and the right team in place." },
  { n: "03", title: "Coordinate", text: "I manage the trades, suppliers and decisions, keeping everything moving calmly." },
  { n: "04", title: "Deliver", text: "A beautifully executed result, finished with care and ready to be lived in." },
];

const reasons = [
  { title: "Calm & Highly Organised", text: "Every moving part is tracked and managed, so you experience the renovation without the stress." },
  { title: "Design-Conscious, Practical", text: "A genuine eye for beautiful spaces, grounded in what works for real homes and real budgets." },
  { title: "One Trusted Point of Contact", text: "From selections to trades, you have a single, reliable partner guiding the entire journey." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <motion.img
          src={EXTERIOR}
          alt="Schonell Projects renovation"
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/45 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/50 to-transparent" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 flex flex-col justify-end pb-20 sm:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease }}
            className="max-w-3xl"
          >
            <Overline className="text-cream/80">Residential Renovation & Project Management</Overline>
            <h1 className="font-serif font-light text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-6">
              Beautifully executed renovations, calmly managed.
            </h1>
            <p className="mt-7 text-cream/85 text-base sm:text-lg font-light leading-relaxed max-w-xl">
              Schonell Projects guides homeowners through thoughtful planning, sourcing, selections
              and coordination — so every decision contributes to a result you'll love.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-enquire-cta"
                className="bg-cream text-ink px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-cream/90 transition-colors"
              >
                Start your enquiry
              </Link>
              <Link
                to="/projects"
                data-testid="hero-projects-cta"
                className="text-cream text-xs uppercase tracking-[0.18em] border-b border-cream/40 hover:border-cream pb-1 transition-colors flex items-center gap-2"
              >
                View projects <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-cream py-24 sm:py-36">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <Reveal><Overline>Welcome</Overline></Reveal>
          <Reveal delay={0.1}>
            <p className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.25] tracking-tight mt-8">
              After renovating my own home, I discovered a passion not only for creating beautiful
              spaces, but for managing the many moving parts that bring a renovation to life.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-muted-foreground text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto">
              Today I work closely with homeowners across Sydney's Lower North Shore, overseeing
              everything from interior selections and material sourcing through to trade coordination
              and project management.
            </p>
          </Reveal>
        </div>
      </section>

      {/* What we do — split */}
      <section className="bg-sand">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[420px] lg:min-h-[640px] overflow-hidden">
            <img src={HOME_FEATURE} alt="Parriwi Project living room" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="flex items-center px-6 sm:px-12 lg:px-20 py-20 lg:py-0">
            <div className="max-w-xl">
              <Reveal><Overline>What we do</Overline></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-6">
                  Taking the stress out of the renovation journey.
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-7 text-muted-foreground font-light leading-relaxed">
                  Whether you're planning a small cosmetic refresh, preparing your property for sale,
                  or a full-scale renovation, my role is to oversee every detail — from sourcing quotes
                  to coordinating trades, stylists, and essential works — ensuring each improvement adds
                  genuine value.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="mt-5 text-muted-foreground font-light leading-relaxed">
                  Calm, organised and design-led — I hold the many threads of your project together
                  so you can enjoy the process with confidence.
                </p>
              </Reveal>
              <Reveal delay={0.4}>
                <Link
                  to="/about"
                  data-testid="home-about-link"
                  className="inline-flex items-center gap-2 mt-9 text-ink text-xs uppercase tracking-[0.18em] border-b border-ink/30 hover:border-ink pb-1 transition-colors"
                >
                  Read my story <ArrowRight size={15} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="bg-cream py-24 sm:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Reveal><Overline>Services</Overline></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-6 max-w-xl">
                  Considered support, at every stage.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="home-services-link"
                className="inline-flex items-center gap-2 text-ink text-xs uppercase tracking-[0.18em] border-b border-ink/30 hover:border-ink pb-1 transition-colors"
              >
                All services <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="bg-cream h-full p-8 lg:p-10 group">
                  <s.icon size={28} strokeWidth={1.2} className="text-ink/70 group-hover:text-ink transition-colors" />
                  <h3 className="font-serif text-2xl text-ink mt-6 leading-snug">{s.title}</h3>
                  <p className="mt-4 text-sm text-muted-foreground font-light leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose — with founder image */}
      <section className="bg-sand">
        <div className="grid lg:grid-cols-2">
          <div className="flex items-center px-6 sm:px-12 lg:px-20 py-20 lg:py-28 order-2 lg:order-1">
            <div className="max-w-xl">
              <Reveal><Overline>Why Schonell Projects</Overline></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-6">
                  A trusted partner through every decision.
                </h2>
              </Reveal>
              <div className="mt-10 space-y-8">
                {reasons.map((r, i) => (
                  <Reveal key={r.title} delay={0.15 + i * 0.1}>
                    <div className="border-l border-stone pl-6">
                      <h3 className="font-serif text-2xl text-ink">{r.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground font-light leading-relaxed">{r.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
          <div className="relative min-h-[480px] lg:min-h-[720px] overflow-hidden order-1 lg:order-2">
            <img src={FOUNDER} alt="Paige Schonell" className="absolute inset-0 h-full w-full object-cover object-top" />
          </div>
        </div>
      </section>

      {/* Featured project preview */}
      <section className="bg-cream py-24 sm:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Overline>Featured project</Overline></Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 relative aspect-[16/9] overflow-hidden group">
              <img
                src={EXTERIOR}
                alt="Parriwi Project"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8 sm:p-12">
                <Overline className="text-cream/80">Mosman</Overline>
                <h3 className="font-serif font-light text-cream text-4xl sm:text-5xl mt-3">Parriwi Project</h3>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 text-center">
              <Link
                to="/projects"
                data-testid="home-projects-cta"
                className="inline-flex items-center gap-2 text-ink text-xs uppercase tracking-[0.18em] border-b border-ink/30 hover:border-ink pb-1 transition-colors"
              >
                Explore the portfolio <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Enquiry CTA */}
      <section className="bg-ink text-cream py-24 sm:py-36">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <Reveal><Overline className="text-cream/60">Let's begin</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-7 leading-[1.1]">
              Planning a renovation? Let's create something beautiful, together.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 text-cream/70 font-light leading-relaxed max-w-xl mx-auto">
              Reaching out early means we can plan thoughtfully and bring the right team together from
              the start. I'd love to hear about your home and what you have in mind.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-enquire"
                className="bg-cream text-ink px-9 py-4 text-xs uppercase tracking-[0.18em] hover:bg-cream/90 transition-colors"
              >
                Enquire now
              </Link>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="text-cream text-xs uppercase tracking-[0.18em] border-b border-cream/40 hover:border-cream pb-1 transition-colors"
              >
                {CONTACT.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
